import { memo, useEffect, useMemo, useState, useCallback } from "react";
import { addDays, addMonths, format, isSameDay, isSameMonth, isWithinInterval, startOfMonth, startOfWeek } from "date-fns";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { usePlanner } from "../contexts/PlannerContext";
import { PlanMyButton, ViewTypeSelector } from "./PlannerControls";
import { SyncButton } from "./SyncButton";

export const MINI_MONTH_WIDTH = 300;

type MiniMonthPickerProps = {
    selectedDate: Date;
    onSelectDate: (date: Date) => void;
};

export const MiniMonthPicker = memo(({ selectedDate, onSelectDate }: MiniMonthPickerProps) => {
    const { settings } = usePlanner();
    const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(selectedDate));
    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);

    useEffect(() => {
        // Keep in sync only when the selected date changes months (avoid overriding manual nav)
        const nextMonthStart = startOfMonth(selectedDate);
        if (!isSameMonth(nextMonthStart, viewMonth)) {
            setViewMonth(nextMonthStart);
        }
    }, [selectedDate]); // intentionally omit viewMonth to allow manual month navigation

    const start = useMemo(() => startOfWeek(viewMonth, { weekStartsOn: 0 }), [viewMonth]);
    const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(start, i)), [start]);

    const handleSelect = useCallback(
        (day: Date) => {
            const next = new Date(day);
            next.setHours(0, 0, 0, 0);
            onSelectDate(next);
        },
        [onSelectDate],
    );

    const headerMonthLabel = format(viewMonth, "MMMM yyyy");
    const weekDayLabels = ["S", "M", "T", "W", "T", "F", "S"];
    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;
    const visibleRange = useMemo(() => {
        if (calendarViewType !== "week" && calendarViewType !== "5day") return null;
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);
        return { start, end: addDays(start, Math.max(0, effectiveWeekDays - 1)) };
    }, [calendarViewType, effectiveWeekDays, selectedDate]);

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex items-center justify-between gap-3" style={{ width: MINI_MONTH_WIDTH }}>
                <ViewTypeSelector />
                <SyncButton />
                <PlanMyButton label="Planner Settings" className="whitespace-nowrap" />
            </div>
            <div className="flex items-center justify-between pt-1" style={{ width: MINI_MONTH_WIDTH }}>
                <div className="flex items-center gap-2 pl-3">
                    <p className="text-sm font-semibold text-theme-0">{headerMonthLabel}</p>
                    <button
                        className="text-xs font-medium text-theme-0 border border-theme rounded-full px-3 py-1 bg-theme-0 hover:bg-theme-1 transition-colors"
                        onClick={() => handleSelect(today)}
                    >
                        Today
                    </button>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        className="h-7 w-7 rounded-md text-theme-0 hover:bg-theme-1 cursor-pointer grid place-items-center"
                        onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                        aria-label="Previous month"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <button
                        className="h-7 w-7 rounded-md text-theme-0 hover:bg-theme-1 cursor-pointer grid place-items-center"
                        onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                        aria-label="Next month"
                    >
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-[8px] text-[12px]" style={{ width: MINI_MONTH_WIDTH }}>
                {weekDayLabels.map((label) => (
                    <span key={label} className="text-center font-medium text-theme-1">{label}</span>
                ))}
                {days.map((day) => {
                    const isCurrentMonth = isSameMonth(day, viewMonth);
                    const isSelected = isSameDay(day, selectedDate);
                    const isToday = isSameDay(day, today);
                    const isInVisibleRange = !!visibleRange && isWithinInterval(day, visibleRange);

                    const baseClasses = "h-7 w-7 rounded-full grid place-items-center cursor-pointer transition-colors justify-self-center";
                    const colorClasses = isSelected
                        ? "bg-theme-link text-white font-semibold"
                        : [
                            isCurrentMonth ? "text-theme-0 hover:bg-theme-1" : "text-theme-1 hover:bg-theme-1/50",
                            isInVisibleRange ? "bg-blue-300/40" : "",
                            isToday ? "border border-theme" : "",
                        ].join(" ").trim();

                    return (
                        <button
                            key={day.toISOString()}
                            className={`${baseClasses} ${colorClasses}`}
                            onClick={() => handleSelect(day)}
                        >
                            {format(day, "d")}
                        </button>
                    );
                })}
            </div>
        </div>
    );
});

MiniMonthPicker.displayName = "MiniMonthPicker";
